import { mapToId, sortByUpdatedAt } from '@witmetrics/utils/arrays';
import { isEmpty } from '@witmetrics/utils/misc';
import { useDispatch, useProjectFormSubmissions } from '@witmetrics/data-store';
import FormProvider from '@/providers/FormProvider';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { useId } from '@/hooks/useId';
import { useString } from '@/hooks/useString';
import { createProjectFormSubmission } from '@/api/utils/projects';
import { updateFormSubmission } from '@/api/utils/forms';
import FormBuilderOverlay, {
  type FormBuilderOverlayProps,
} from '@/overlays/FormBuilderOverlay';
import Section from '@/overlays/CaseOverlay/Section';
import EmptyMessage from '@/overlays/CaseOverlay/EmptyMessage';
import FormsHeader from '@/components/FormsHeader';
import FormSubmissionsList from '@/components/FormSubmissionsList';

export type FormsSectionProps = {
  isFetchingData?: boolean;
};

export default function FormsSection({ isFetchingData }: FormsSectionProps) {
  const dispatch = useDispatch();
  const { activeProjectId } = useAppState();
  const submissions = useProjectFormSubmissions(activeProjectId);
  const [submissionId, setSubmissionId, { clear: clearSubmissionId }] = useId();
  const [search, setSearch] = useString();
  const [isBuilderOpen, toggleBuilder] = useToggle();

  const handleOpenSubmission = (id: number) => {
    setSubmissionId(id);
    toggleBuilder(true);
  };

  const handleStartNew = () => {
    clearSubmissionId();
    toggleBuilder(true);
  };

  const handleSave: FormBuilderOverlayProps['onSave'] = async ({
    versionId,
    answers,
  }) => {
    if (submissionId) {
      const submission = await updateFormSubmission(submissionId, answers);
      dispatch.addSubmissions([submission]);
    } else {
      const result = await createProjectFormSubmission(
        activeProjectId!,
        versionId,
        answers
      );
      dispatch.addSubmissions([result.submission]);
      dispatch.addProjectSubmissions([result.projectSubmission]);
    }
  };

  if (!activeProjectId) return null;

  const submissionIds = mapToId(sortByUpdatedAt(submissions));

  return (
    <>
      {isBuilderOpen && (
        <FormProvider initialSubmissionId={submissionId}>
          <FormBuilderOverlay
            submissionId={submissionId}
            onSave={handleSave}
            onClose={() => toggleBuilder(false)}
          />
        </FormProvider>
      )}
      <Section>
        <FormsHeader
          search={search}
          onSearch={setSearch}
          onAddNew={handleStartNew}
        />
        {isEmpty(submissionIds) ? (
          <EmptyMessage>No form submissions</EmptyMessage>
        ) : (
          <FormSubmissionsList
            isFetchingData={isFetchingData}
            submissionIds={submissionIds}
            onSelect={handleOpenSubmission}
          />
        )}
      </Section>
    </>
  );
}
